import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Badge } from "../badge/badge";
import { usePortal } from "../../hooks/use-portal";
import "./multi-select-input.scss";

export interface MultiSelectInputOption {
  label: string;
  value: string;
}

export interface MultiSelectInputProps {
  label?: string;
  options: MultiSelectInputOption[];
  /** Controlled selected values */
  value?: string[];
  /** Initial selected values for uncontrolled usage */
  defaultValue?: string[];
  placeholder?: string;
  onChange?: (values: string[], options: MultiSelectInputOption[]) => void;
  disabled?: boolean;
  focusBorderWidth?: string;
  className?: string;
  inputClassName?: string;
  /**
   * Optional ref to the trigger element. Accepts RefObject or callback ref.
   * Merged with the internal ref used for positioning and outside-click.
   */
  triggerRef?: React.Ref<HTMLDivElement>;
  /**
   * When provided, the dropdown portals into this element via `usePortal`.
   * Useful inside modals/drawers so the menu stays in the correct stacking
   * context. When omitted, the dropdown portals to `document.body`.
   */
  targetRef?: React.RefObject<HTMLElement | null>;
}

function mergeRefs<T>(
  ...refs: (React.Ref<T> | undefined)[]
): (value: T | null) => void {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transform: open ? "rotate(180deg)" : undefined,
      transition: "transform 0.2s ease",
    }}
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TickIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M16.6667 5L7.5 14.1667L3.33334 10"
      stroke="#1F1F1F"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BadgeCloseIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  label,
  options,
  value: controlledValue,
  defaultValue = [],
  placeholder = "Select...",
  onChange,
  disabled = false,
  focusBorderWidth,
  className = "",
  inputClassName = "",
  triggerRef: externalTriggerRef,
  targetRef,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<string[]>(defaultValue);
  const selectedValues = isControlled ? controlledValue : internalValue;

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">(
    "bottom"
  );
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const internalTriggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const getPortalContainer = useCallback(
    () => targetRef?.current ?? null,
    [targetRef]
  );
  const renderInPortal = usePortal(getPortalContainer);

  const setSelectedValues = useCallback(
    (next: string[]) => {
      if (!isControlled) {
        setInternalValue(next);
      }
      const selectedOptions = options.filter((o) => next.includes(o.value));
      onChange?.(next, selectedOptions);
    },
    [isControlled, onChange, options]
  );

  const selectedOptions = useMemo(
    () => options.filter((o) => selectedValues.includes(o.value)),
    [options, selectedValues]
  );

  const selectedSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues]
  );

  const updateDropdownPosition = useCallback(() => {
    if (!internalTriggerRef.current) return;
    const triggerRect = internalTriggerRef.current.getBoundingClientRect();
    const gap = 0;

    if (targetRef?.current) {
      const containerRect = targetRef.current.getBoundingClientRect();
      const scrollTop = targetRef.current.scrollTop;
      const scrollLeft = targetRef.current.scrollLeft;
      const spaceBelow = containerRect.bottom - triggerRect.bottom - gap;
      const spaceAbove = triggerRect.top - containerRect.top - gap;
      const openAbove =
        spaceBelow < containerRect.height * 0.45 && spaceAbove > spaceBelow;

      setDropdownPosition(openAbove ? "top" : "bottom");
      setPortalStyle({
        position: "absolute",
        left: triggerRect.left - containerRect.left + scrollLeft,
        width: triggerRect.width,
        zIndex: 99999,
        ...(openAbove
          ? {
              bottom:
                containerRect.bottom - triggerRect.top + scrollTop - gap,
            }
          : {
              top: triggerRect.bottom - containerRect.top + scrollTop + gap,
            }),
      });
    } else {
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const openAbove = spaceBelow < window.innerHeight * 0.45;

      setDropdownPosition(openAbove ? "top" : "bottom");
      setPortalStyle({
        position: "fixed",
        left: triggerRect.left,
        width: triggerRect.width,
        zIndex: 99999,
        ...(openAbove
          ? { bottom: window.innerHeight - triggerRect.top }
          : { top: triggerRect.bottom }),
      });
    }
  }, [targetRef]);

  const toggleOpen = () => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
    } else {
      updateDropdownPosition();
      setIsOpen(true);
    }
  };

  const handleOptionToggle = (option: MultiSelectInputOption) => {
    if (disabled) return;
    const next = selectedSet.has(option.value)
      ? selectedValues.filter((v) => v !== option.value)
      : [...selectedValues, option.value];
    setSelectedValues(next);
  };

  const handleBadgeRemove = (value: string) => {
    if (disabled) return;
    setSelectedValues(selectedValues.filter((v) => v !== value));
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        internalTriggerRef.current &&
        !internalTriggerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => updateDropdownPosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [isOpen, updateDropdownPosition]);

  const triggerDisplay =
    selectedValues.length > 0
      ? `${selectedValues.length} selected`
      : null;

  const dropdown = isOpen ? (
    <div
      className={`multi-select-input__dropdown multi-select-input__dropdown--${dropdownPosition}`}
      style={portalStyle}
      ref={dropdownRef}
      role="listbox"
      aria-multiselectable="true"
      aria-label={label}
    >
      {options.map((option) => {
        const isSelected = selectedSet.has(option.value);
        return (
          <div
            key={option.value}
            className={`multi-select-input__option ${isSelected ? "multi-select-input__option--selected" : ""}`}
            onClick={() => handleOptionToggle(option)}
            role="option"
            aria-selected={isSelected}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleOptionToggle(option);
              } else if (e.key === "Escape") {
                setIsOpen(false);
                internalTriggerRef.current?.focus();
              }
            }}
          >
            {option.label}
            {isSelected && <TickIcon />}
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <div
      className={`multi-select-input ${className}`}
      style={
        focusBorderWidth
          ? ({
              "--multi-select-input-focus-border-width": focusBorderWidth,
            } as React.CSSProperties)
          : undefined
      }
    >
      {label && (
        <label className="multi-select-input__label">{label}</label>
      )}

      <div
        className={`multi-select-input__trigger ${isOpen ? "multi-select-input__trigger--open" : ""} ${disabled ? "multi-select-input__trigger--disabled" : ""} ${inputClassName}`}
        onClick={toggleOpen}
        ref={mergeRefs(internalTriggerRef, externalTriggerRef)}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleOpen();
          } else if (e.key === "Escape") {
            setIsOpen(false);
          }
        }}
      >
        {triggerDisplay ? (
          <span className="multi-select-input__selected-value">
            {triggerDisplay}
          </span>
        ) : (
          <span className="multi-select-input__placeholder">{placeholder}</span>
        )}
        <span className="multi-select-input__icon" aria-hidden="true">
          <ChevronIcon open={isOpen} />
        </span>
      </div>

      {selectedOptions.length > 0 && (
        <div className="multi-select-input__badges" role="list">
          {selectedOptions.map((option) => (
            <div
              key={option.value}
              className="multi-select-input__badge-item"
              role="listitem"
            >
              <Badge
                text={option.label}
                size="small"
                rightIcon={!disabled ? <BadgeCloseIcon /> : undefined}
                onRightIconClick={
                  !disabled
                    ? () => handleBadgeRemove(option.value)
                    : undefined
                }
              />
            </div>
          ))}
        </div>
      )}

      {isOpen ? renderInPortal(dropdown) : null}
    </div>
  );
};
